/**
 * TranscriptDialogHost (KAN-710)
 *
 * The one place the client conversation modal is rendered: it follows
 * ?dialog=<clientId> / ?estimate=<estimateId> (useDialogParam), so a shared
 * link opens the modal on any page under DashboardLayout. Callers only call
 * openDialog. What the caller did not pass in the hint is loaded here: the
 * client's name for the title and, without an estimate in the URL, the
 * client's latest estimate with messages (manual messages go there).
 */
import { useDialogParam, type DialogHint } from '@/hooks/useDialogParam'
import { useClientConversationTarget } from '@/components/clients/useClientConversationTarget'
import { ContactTranscriptDialog } from './ContactTranscriptDialog'

const FALLBACK_TITLE = 'Переписка'

interface UrlTranscriptDialogProps {
  clientId: string | null
  estimateId: string | null
  hint: DialogHint | undefined
  onClose: () => void
}

function UrlTranscriptDialog({
  clientId,
  estimateId,
  hint,
  onClose,
}: UrlTranscriptDialogProps) {
  const target = useClientConversationTarget({
    clientId,
    estimateId,
    loadClient: !hint?.title,
  })
  const contactId = target.contactId
  const title = hint?.title || target.title || FALLBACK_TITLE

  return (
    <ContactTranscriptDialog
      contactId={contactId}
      title={title}
      clientId={clientId}
      manualMode={hint?.manualMode}
      senderAccount={hint?.senderAccount}
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    />
  )
}

export function TranscriptDialogHost() {
  const { clientId, estimateId, hint, isOpen, closeDialog } = useDialogParam()
  if (!isOpen) return null
  return (
    <UrlTranscriptDialog
      key={`${clientId ?? ''}|${estimateId ?? ''}`}
      clientId={clientId}
      estimateId={estimateId}
      hint={hint}
      onClose={closeDialog}
    />
  )
}
